// cellStyles.js — shared sizing for the inline-edit input primitives.
// One place so TextCell / SelectCell / CheckboxCell / FormulaCell align pixel-for-pixel
// under their columns (CO3). Theme tokens only — no raw hex/fontSize/monospace.

import { KINDS } from './Text.jsx';

// cellFont(kind) — THE font of a grid cell, read AND edit, from its column's text kind.
// Alex, 2026-10-15: "in Edit mode, the font is generally smaller instead of the same."
// It was: read cells took the grid's 13px, every edit control a fixed fs-small. The
// answer he chose is "match cell style", not "match cell size" — a money cell reads
// at 11px and a code at 13px mono bold, so one fixed input size is wrong somewhere
// whichever size it is. So a column declares `textKind` (a row of Text's KINDS) and
// its read renderer draws with `<Text kind>` of the same name; DataGrid puts this on the
// cell, and every edit control takes it through the --tg-cell-* tokens below. No kind =
// the grid's body text. Only the tokens, not the td's own font: a mono td changes the
// row's line box (measured: Catalog rows 39 -> 37px), so read mode would move.
export function cellFont(kind) {
  if (!kind) return { '--tg-cell-fs': 'var(--th-font-size)' };
  const k = KINDS[kind];
  if (!k) throw new Error(`cellFont: unknown text kind "${kind}" (known: ${Object.keys(KINDS).join(', ')})`);
  return {
    '--tg-cell-fs': k.fontSize,
    '--tg-cell-ff': k.fontFamily || 'var(--ff-body)',
    '--tg-cell-fw': k.fontWeight || 400,
  };
}

// The font every edit control takes: its cell's, via cellFont; outside a grid cell the
// theme's root values (the fs-small every control had before).
export const CELL_FONT = {
  fontSize: 'var(--tg-cell-fs)',
  fontFamily: 'var(--tg-cell-ff)',
  fontWeight: 'var(--tg-cell-fw)',
};

// Base controlled-input style, shared by TextCell / SelectCell / FormulaCell.
// Matches the input style DataGrid previously inlined (full-width, thin accent border).
// Font and border deliberately point at the SAME tokens CascadeDropdown's own
// btnBase uses (CELL_FONT, --tg-filter-border), not new values — Alex, 2026-08-22:
// "merge all that to the same style size... I prefer the Cascade dropdown". Padding
// stays on --th-cell-inset (see below) — that one's tuned for a different invariant.
export const CELL_INPUT_STYLE = {
  width: '100%',
  boxSizing: 'border-box',
  ...CELL_FONT,
  border: 'var(--tg-filter-border)',
  // The cell's text inset carried INSIDE the control (border + this = --th-cell-pad),
  // so edit-mode text lands on the same pixel as read-mode text while the box still
  // spans the full cell. Alex, 2026-08-03: "padding in the cell but not around".
  padding: 'var(--th-cell-inset, 3px 7px)',
  // Match the text line box a read cell gets; without it Chrome gives a dropdown
  // control a 17px intrinsic line box against a span's 15px, so it renders 2px taller.
  // (Deliberately NOT writing the element name in angle brackets here: check:style
  // greps for it and cannot tell code from a comment — this line was failing the gate.)
  lineHeight: 'var(--th-cell-line-h, 1.15)',
  outline: 'none',
  background: 'var(--th-row-even)',
  color: 'var(--th-text-body)',
};
